/**
 * Application Home Component
 *
 * Main component for managing invitations in the admin interface.

 */
import React, { useState, useEffect } from 'react';
import InvitationTable from './InvitationTable';
import InvitationErrors from '../errors/InvitationErrors';
import CreateInvitationStep1 from './createInvitation/step1/CreateInvitationStep1';
import CreateInvitationStep2 from './createInvitation/step2/CreateInvitationStep2';
import CreateInvitationStep3 from './createInvitation/step3/CreateInvitationStep3';
import InvitationIssueResultModal from './createInvitation/InvitationIssueResultModal';
import EditInvitationPanel from './EditInvitationPanel';
import { Participant } from './createInvitation/step1/types';
import { Invitation } from './types';
import { API_BASE_URL } from '../../../../config';
import {
  sendInvitations,
  type InvitationDeliveryMode,
  type RenderedInvitationMessage,
} from '../../../../services/invitationSendApi';
import {
  normalizeFormCode,
  pruneEmployerContext,
  type FormEmployerContext,
} from './createInvitation/invitationEmployerFields';
import { matchesInvitationPeriod } from './invitationListFilters';
import './ApplicationHome.css';
import createInvitationIcon from '../../../../assests/icons/createInvitation_icon.png';
import sentFailIcon from '../../../../assests/icons/sentFail_icon.png';
import filterIcon from '../../../../assests/icons/filter_icon.png';
import successIcon from '../../../../assests/icons/success.png';
import failIcon from '../../../../assests/icons/fail.png';

//the properties passed to ApplicationHome component
export interface ApplicationHomeProps {
  /** an array ofInvitation objects */
  invitations?: Invitation[];
  /** Callback function when create invitation button is clicked,this exist in the parent component,Admin route */
  onCreateInvitation?: () => void;
  /** Callback when error button is clicked */
  onErrorClick?: () => void;

    /** When true (e.g. from dashboard notification), show 招待送信エラー on mount */
  initialShowErrorsView?: boolean;
  /** Called once after opening the errors view from initialShowErrorsView */
  onErrorsViewOpened?: () => void;
  /** When true (e.g. from dashboard notification), show 招待を作成 on mount */
  initialShowCreateInvitationView?: boolean;
  /** Called once after opening the create invitation view from initialShowCreateInvitationView */
  onCreateInvitationViewOpened?: () => void;
  /** Prefill Step 1 selected list (e.g. after prepare-rehire) */
  initialRehireParticipant?: { email: string; name: string } | null;
  /** Called once after applying initialRehireParticipant */
  onRehireParticipantConsumed?: () => void;
  /** Open メッセージ tab for an employee (e.g. from invitation edit). */
  onOpenMessages?: (email: string, invitationId?: string) => void;
}

//the properties are passed to ApplicationHome component as ApplicationHomeProps
const ApplicationHome: React.FC<ApplicationHomeProps> = ({
  invitations = [],
  onCreateInvitation,
  onErrorClick,
  initialShowErrorsView,
  onErrorsViewOpened,
  initialShowCreateInvitationView,
  onCreateInvitationViewOpened,
  initialRehireParticipant,
  onRehireParticipantConsumed,
  onOpenMessages,
}) => {
  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------
  const [bounceCount, setBounceCount] = useState<number>(0);
  const [showErrorsView, setShowErrorsView] = useState(false);
  const [showCreateInvitationView, setShowCreateInvitationView] = useState(false);
  const [createInvitationStep, setCreateInvitationStep] = useState<1 | 2 | 3>(1);
  const [selectedParticipantsForInvitation, setSelectedParticipantsForInvitation] = useState<Participant[]>([]);
  const [availableParticipantsForInvitation, setAvailableParticipantsForInvitation] = useState<Participant[]>([]);
  const[selectedDocumentsForInvitation,setSelectedDocumentsForInvitation]=useState<string[]>([]);
  const [selectedFormCodesForInvitation, setSelectedFormCodesForInvitation] = useState<string[]>([]);
  const [formEmployerContextForInvitation, setFormEmployerContextForInvitation] =
    useState<FormEmployerContext>({});
  const [step2ValidationError, setStep2ValidationError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    period: '',
    status: '',
  });
  /** Table rows from empaccount (sent, draft, submitted) — same source as dashboard table */
  const [invitationList, setInvitationList] = useState<Invitation[]>([]);
  //State for sending invitation,result message and error message
  const [sendingInvitation, setSendingInvitation] = useState(false);
  const [sendResultMessage, setSendResultMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [issueResultModal, setIssueResultModal] = useState<{
    deliveryMode: InvitationDeliveryMode;
    sent: number;
    failed: number;
    errors: string[];
    messages: RenderedInvitationMessage[];
  } | null>(null);
  const [editingInvitation, setEditingInvitation] = useState<Invitation | null>(null);
  const [listRefreshKey, setListRefreshKey] = useState(0);

  const displayInvitations = invitations.length > 0 ? invitations : invitationList;
  const filteredInvitations = displayInvitations.filter((inv) => {
    const query = searchQuery.trim().toLowerCase();
    const matchesSearch = !query || inv.name.toLowerCase().includes(query);
    const matchesStatus = !filters.status || inv.status === filters.status;
    const matchesPeriod = matchesInvitationPeriod(inv.invitationSentDate, filters.period);
    return matchesSearch && matchesStatus && matchesPeriod;
  });

  // ---------------------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------------------

  // Load bounce (invitation send error) count for the error button badge when on main view
  useEffect(() => {
    if (showErrorsView) return;
    const loadBounceCount = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/bounces/count`);
        if (res.ok) {
          const data = await res.json();
          setBounceCount(typeof data.count === 'number' ? data.count : Number(data.count) || 0);
        } else {
          setBounceCount(0);
        }
      } catch {
        setBounceCount(0);
      }
    };
    loadBounceCount();
  }, [showErrorsView]);

  useEffect(() => {
  if (initialShowErrorsView) {
    setShowErrorsView(true);
    onErrorsViewOpened?.();
  }
  }, [initialShowErrorsView, onErrorsViewOpened]);

  useEffect(() => {
    if (initialShowCreateInvitationView) {
      setShowCreateInvitationView(true);
      onCreateInvitationViewOpened?.();
    }
  }, [initialShowCreateInvitationView, onCreateInvitationViewOpened]);

  useEffect(() => {
    if (!initialRehireParticipant?.email?.trim()) return;
    setShowCreateInvitationView(true);
    setCreateInvitationStep(1);
    setSelectedParticipantsForInvitation([
      {
        email: initialRehireParticipant.email.trim(),
        name: initialRehireParticipant.name?.trim() || initialRehireParticipant.email.trim(),
      },
    ]);
    setSelectedDocumentsForInvitation([]);
    setSelectedFormCodesForInvitation([]);
    setFormEmployerContextForInvitation({});
    setStep2ValidationError(null);
    setSendResultMessage(null);
    onRehireParticipantConsumed?.();
  }, [initialRehireParticipant, onRehireParticipantConsumed]);

  // Load invitation table from empaccount (sent, draft, submitted) — same as dashboard table
  useEffect(() => {
    if (showErrorsView || showCreateInvitationView || editingInvitation) return;
    let cancelled = false;
    fetch(`${API_BASE_URL}/api/employees/dashboard-list`)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('Failed to load list'))))
      .then((rows: {
        id: string;
        name: string;
        notificationSentDate: string;
        status: string;
        statusUpdateDate: string;
        statusUpdateTime: string;
        submissionDeadline?: string;
        invitationId?: string;
        email?: string;
        accountStatus?: string;
      }[]) => {
        if (cancelled) return;
        const toInvitationStatus = (s: string): Invitation['status'] => {
          if (s === 'sent') return 'unopened';
          if (s === 'draft') return 'in-progress';
          if (s === 'submitted') return 'completed';
          if (s === 'resubmission_requested') return 'resubmission-requested';
          if (s === 'resubmitted') return 'resubmitted';
          return 'in-progress';
        };
        const list: Invitation[] = (Array.isArray(rows) ? rows : []).map((row) => ({
          id: row.id,
          name: row.name || '',
          invitationSentDate: row.notificationSentDate || '',
          status: toInvitationStatus(row.status),
          statusUpdateTime: row.statusUpdateTime || '',
          submissionDeadline: row.submissionDeadline || '',
          invitationId: row.invitationId || '',
          email: row.email || '',
          accountStatus: row.accountStatus || row.status,
        }));
        setInvitationList(list);
      })
      .catch(() => {
        if (!cancelled) setInvitationList([]);
      });
    return () => { cancelled = true; };
  }, [showErrorsView, showCreateInvitationView, editingInvitation, listRefreshKey]);

  // Load available participants when creating invitation — only employees with status = registered (not yet sent invitation)
  useEffect(() => {
    if (!showCreateInvitationView) return;
    const loadRegisteredEmployees = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/employees/registered`);
        if (res.ok) {
          const data = await res.json();
          setAvailableParticipantsForInvitation(
            (Array.isArray(data) ? data : []).map((emp: { emailaddress: string; name: string }) => ({
              email: emp.emailaddress,
              name: emp.name,
            }))
          );
        } else {
          setAvailableParticipantsForInvitation([]);
        }
      } catch {
        setAvailableParticipantsForInvitation([]);
      }
    };
    loadRegisteredEmployees();
  }, [showCreateInvitationView]);

  // ---------------------------------------------------------------------------
  // Handlers – search & filter
  // ---------------------------------------------------------------------------
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
  };

  // ----------
  const handleFilterChange = (filterType: 'period' | 'status', value: string) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
  };

  // ---------------------------------------------------------------------------
  // Handlers – errors view
  // ---------------------------------------------------------------------------
  const handleErrorClick = () => {
    setShowErrorsView(true);
    if (onErrorClick) onErrorClick();
  };

  // ----------
  const handleBackFromErrors = () => {
    setShowErrorsView(false);
  };

  // ----------
  const handleRetryAll = () => {
    console.log('Retry all failed invitations');
  };

  // ---------------------------------------------------------------------------
  // Handlers – create invitation flow
  // ---------------------------------------------------------------------------
  const handleCreateInvitationClick = () => {
    setShowCreateInvitationView(true);
    setCreateInvitationStep(1);
    setSelectedParticipantsForInvitation([]);// reset selected participants
    setSelectedDocumentsForInvitation([]);// reset selected documents
    setSelectedFormCodesForInvitation([]);
    setFormEmployerContextForInvitation({});
    setStep2ValidationError(null);
    //If a function called onCreateInvitation was provided, then call it.
    if (onCreateInvitation) onCreateInvitation();
    setSendResultMessage(null);
  };

  // ----------
  const handleBackFromCreateInvitation = () => {
    setShowCreateInvitationView(false);
    setCreateInvitationStep(1);
  };

  // ----------
  const handleSaveAndProceedStep1 = (selectedParticipants: Participant[]) => {
    setSelectedParticipantsForInvitation(selectedParticipants);
    setCreateInvitationStep(2);

    //Testing purpose only
    console.log('Selected participants for invitation:', selectedParticipants.map((p) => p.email));
  };

  // ----------
  const handleBackToStep1FromStep2 = () => {
    setCreateInvitationStep(1);
  };

  // ----------
  const handleBackFromStep2 = () => {
    setShowCreateInvitationView(false);
    setCreateInvitationStep(1);
  };

  const handleFormEmployerContextChange = (formCode: string, fields: Record<string, string>) => {
    const normalized = normalizeFormCode(formCode);
    if (!normalized) return;
    setFormEmployerContextForInvitation((prev) => ({
      ...prev,
      [normalized]: fields,
    }));
  };

  const handleFormEmployerContextClear = (formCode: string) => {
    const normalized = normalizeFormCode(formCode);
    if (!normalized) return;
    setFormEmployerContextForInvitation((prev) => {
      if (!prev[normalized]) return prev;
      const next = { ...prev };
      delete next[normalized];
      return next;
    });
  };

  const handleSelectedFormCodesChange = (formCodes: string[]) => {
    setSelectedFormCodesForInvitation(formCodes);
    setFormEmployerContextForInvitation((prev) => pruneEmployerContext(formCodes, prev));
  };

  // ----------
  const handleSaveAndProceedStep2 = () => {
    setStep2ValidationError(null);
    setCreateInvitationStep(3);
  };

  // ----------
  const handleBackToStep2FromStep3 = () => {
    setCreateInvitationStep(2);
  };

  // ----------
  const handleBackFromStep3 = () => {
    setShowCreateInvitationView(false);
    setCreateInvitationStep(1);
  };

  // ----------
  const handleInvitationClick = (invitation: Invitation) => {
    if (!invitation.invitationId) return;
    setEditingInvitation(invitation);
  };

  const handleBackFromEditInvitation = () => {
    setEditingInvitation(null);
  };

  const handleEditInvitationSaved = () => {
    setEditingInvitation(null);
    setListRefreshKey((k) => k + 1);
  };

  const handleSendInvitation = async (payload: {
    subject: string;
    body: string;
    deadline: string;
    recipientIds: string[];
    deliveryMode: InvitationDeliveryMode;
  }) => {
    const recipients = selectedParticipantsForInvitation.map((p) => ({
      email: p.email,
      name: p.name,
    }));

    setSendingInvitation(true);
    setSendResultMessage(null);
    try {
      const data = await sendInvitations({
        subject: payload.subject,
        body: payload.body,
        deadline: payload.deadline,
        recipients,
        docCodes: selectedDocumentsForInvitation,
        formCodes: selectedFormCodesForInvitation,
        formEmployerContext: pruneEmployerContext(
          selectedFormCodesForInvitation,
          formEmployerContextForInvitation
        ),
        deliveryMode: payload.deliveryMode,
      });

      setShowCreateInvitationView(false);
      setCreateInvitationStep(1);
      setFormEmployerContextForInvitation({});
      setStep2ValidationError(null);
      setListRefreshKey((k) => k + 1);

      const messages = data.renderedMessages ?? [];
      const deliveryMode = data.deliveryMode ?? payload.deliveryMode;

      if (messages.length > 0) {
        setIssueResultModal({
          deliveryMode,
          sent: data.sent,
          failed: data.failed,
          errors: data.errors,
          messages,
        });
        setSendResultMessage(null);
      } else if (data.failed === 0 && data.sent > 0) {
        setSendResultMessage({
          type: 'success',
          text:
            deliveryMode === 'MANUAL'
              ? `${data.sent}件発行しました。`
              : `${data.sent}件送信しました。`,
        });
      } else if (data.sent > 0) {
        setSendResultMessage({
          type: 'error',
          text: `${data.sent}件成功、${data.failed}件失敗。`,
        });
      } else {
        setSendResultMessage({
          type: 'error',
          text: data.errors[0] ?? '送信に失敗しました。',
        });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : '送信に失敗しました。';
      setSendResultMessage({ type: 'error', text: message });
    } finally {
      setSendingInvitation(false);
    }
  };

  const handleCloseIssueResultModal = () => {
    setIssueResultModal(null);
    setListRefreshKey((k) => k + 1);
  };

  // ---------------------------------------------------------------------------
  // Render – edit invitation
  // ---------------------------------------------------------------------------
  if (editingInvitation?.invitationId) {
    return (
      <EditInvitationPanel
        invitationId={editingInvitation.invitationId}
        focusEmail={editingInvitation.email ?? ''}
        focusName={editingInvitation.name}
        onBack={handleBackFromEditInvitation}
        onSaved={handleEditInvitationSaved}
        onOpenMessages={onOpenMessages}
      />
    );
  }

  // ---------------------------------------------------------------------------
  // Render – create invitation steps
  // ---------------------------------------------------------------------------
  if (showCreateInvitationView) {
    if (createInvitationStep === 3) {
      return (
        <CreateInvitationStep3
        selectedParticipants={selectedParticipantsForInvitation}
        onBackToStep2={handleBackToStep2FromStep3}
        onBack={handleBackFromStep3}
        onSend={handleSendInvitation}
        sending={sendingInvitation}
/>
      );
    }
    if (createInvitationStep === 2) {
      return (
        <CreateInvitationStep2
          selectedParticipants={selectedParticipantsForInvitation}
          selectedDocuments={selectedDocumentsForInvitation}
          onDocumentCopyChange={setSelectedDocumentsForInvitation}
          selectedFormCodes={selectedFormCodesForInvitation}
          onFormCodesChange={handleSelectedFormCodesChange}
          formEmployerContext={formEmployerContextForInvitation}
          onFormEmployerContextChange={handleFormEmployerContextChange}
          onFormEmployerContextClear={handleFormEmployerContextClear}
          step2ValidationError={step2ValidationError}
          onClearStep2ValidationError={() => setStep2ValidationError(null)}
          onBackToStep1={handleBackToStep1FromStep2}
          onBack={handleBackFromStep2}
          onSaveAndProceed={handleSaveAndProceedStep2}
        />
      );
    }
    return (
      <CreateInvitationStep1
        availableParticipants={availableParticipantsForInvitation}
        initialSelected={selectedParticipantsForInvitation}
        onBack={handleBackFromCreateInvitation}
        onSaveAndProceed={handleSaveAndProceedStep1}
      />
    );
  }

  // ---------------------------------------------------------------------------
  // Render – errors view
  // ---------------------------------------------------------------------------
  if (showErrorsView) {
    return (
      <InvitationErrors
        onBack={handleBackFromErrors}
        onRetry={handleRetryAll}
      />
    );
  }

  // ---------------------------------------------------------------------------
  // Render – main (invitation management home)
  // ---------------------------------------------------------------------------
  return (
    <div className="application-home">
      {sendResultMessage && (
        <div
          className="application-home__result-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="send-result-title"
        >
          <div className="application-home__result-popup">
            <img
              src={sendResultMessage.type === 'success' ? successIcon : failIcon}
              alt=""
              className="application-home__result-icon"
              aria-hidden
            />
            <h2 id="send-result-title" className="application-home__result-title">
              {sendResultMessage.type === 'success' ? '送信完了' : '送信エラー'}
            </h2>
            <p className="application-home__result-text">{sendResultMessage.text}</p>
            {sendResultMessage.type === 'error' && (
            <p className="application-home__result-hint">
              詳細は「招待送信エラー」をご確認ください。
            </p>
)}
            <button
              type="button"
              className="application-home__result-close-btn"
              onClick={() => setSendResultMessage(null)}
            >
              閉じる
            </button>
          </div>
        </div>
      )}


      <h1 className="application-home__title">招待</h1>
      
      <div className="application-home__actions">
      
        <button
          className="application-home__btn application-home__btn--create"
          onClick={handleCreateInvitationClick}
        >
          <img src={createInvitationIcon} alt="" className="application-home__btn-icon" aria-hidden />
          <span>招待を作成</span>
        </button>
        <button
          className="application-home__btn application-home__btn--error"
          onClick={handleErrorClick}
        >
          <img src={sentFailIcon} alt="" className="application-home__btn-icon" aria-hidden />
          <span>招待送信エラー</span>
          {bounceCount > 0 && (
            <span className="application-home__error-badge">{bounceCount}</span>
          )}
        </button>
      </div>

      <div className="application-home__controls">
        <form className="application-home__search" onSubmit={handleSearch}>
          <input
            type="text"
            className="application-home__search-input"
            placeholder=""
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button type="submit" className="application-home__search-btn">
            検索
          </button>
        </form>

        <div className="application-home__filter-wrapper">
          <button
            className="application-home__filter-btn"
            onClick={() => setShowFilters(!showFilters)}
            aria-label="Filter options"
          >
            <img src={filterIcon} alt="" className="application-home__filter-icon" aria-hidden />
          </button>

          {showFilters && (
            <div className="application-home__filter-dropdown">
              <div className="application-home__filter-group">
                <label>期間</label>
                <select
                  value={filters.period}
                  onChange={(e) => handleFilterChange('period', e.target.value)}
                >
                  <option value="">すべて</option>
                  <option value="yearly">年次</option>
                  <option value="monthly">月次</option>
                </select>
              </div>
              <div className="application-home__filter-group">
                <label>ステータス</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <option value="">すべて</option>
                  <option value="unopened">未開封</option>
                  <option value="in-progress">進行中</option>
                  <option value="resubmission-requested">再提出依頼中</option>
                  <option value="completed">完了</option>
                  <option value="resubmitted">再提出済み</option>
                </select>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="application-home__table-area">
        <InvitationTable
          invitations={filteredInvitations}
          onInvitationClick={handleInvitationClick}
        />
      </div>

      {issueResultModal && (
        <InvitationIssueResultModal
          deliveryMode={issueResultModal.deliveryMode}
          sent={issueResultModal.sent}
          failed={issueResultModal.failed}
          errors={issueResultModal.errors}
          messages={issueResultModal.messages}
          onClose={handleCloseIssueResultModal}
        />
      )}
    </div>
  );
};

export default ApplicationHome;
